// RecordDetail.tsx — single reference data record with tabs.
// Loads the real record from the API based on URL params.

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { RecordRow } from "@/lib/types";
import { fmtRelative } from "@/lib/formatters";

type Tone = "primary" | "secondary" | "tertiary" | "danger" | "neutral";

const toneColors: Record<Tone, { bg: string; fg: string }> = {
  primary:   { bg: "var(--md-primary-container)",   fg: "var(--md-on-primary-container)"   },
  secondary: { bg: "var(--md-secondary-container)", fg: "var(--md-on-secondary-container)" },
  tertiary:  { bg: "var(--md-tertiary-container)",  fg: "var(--md-on-tertiary-container)"  },
  danger:    { bg: "var(--md-error-container)",     fg: "var(--md-on-error-container)"     },
  neutral:   { bg: "var(--md-sc-low)",              fg: "var(--md-on-surface-var)"         },
};

function Pill({ children, tone = "neutral", mono = false }: { children: ReactNode; tone?: Tone; mono?: boolean }) {
  const c = toneColors[tone];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: "var(--r-pill)",
      background: c.bg, color: c.fg,
      font: mono ? "var(--t-mono-sm)" : "var(--t-label-md)",
      lineHeight: 1.4, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      background: "var(--md-sc-lowest)",
      border: "1px solid var(--md-outline-var)",
      borderRadius: "var(--r-lg)",
      boxShadow: "var(--e-1)",
      padding: 24,
      ...style,
    }}>{children}</div>
  );
}

function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  const initials = (name || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
  const hue = ((name?.charCodeAt(0) ?? 0) * 17) % 360;
  return (
    <span aria-hidden style={{
      width: size, height: size,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      borderRadius: "var(--shape-squircle)",
      background: `hsl(${hue} 30% 60%)`,
      color: "#fff",
      font: "var(--t-label-md)",
      letterSpacing: ".02em",
    }}>{initials}</span>
  );
}

const STATUS_TONE: Record<string, Tone> = {
  live: "secondary", approved: "secondary", pending: "tertiary",
  draft: "neutral", conflict: "danger", deprecated: "neutral", rejected: "danger",
};

const TABS = ["Overview", "Fields", "Related", "Lineage", "Versions", "Audit"] as const;
type Tab = typeof TABS[number];

export function RecordDetail() {
  const { domain = "instruments", recordId = "" } = useParams();
  const [rec, setRec] = useState<RecordRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("Overview");
  const [moreOpen, setMoreOpen] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRec(null);
    setTab("Overview");
    if (!recordId) { setLoading(false); setError("No record id"); return; }
    api.records.get(domain, recordId)
      .then(r => { if (!cancelled) { setRec(r); setLoading(false); } })
      .catch(e => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [domain, recordId]);

  const onEdit = () => { setTab("Fields"); toast("Editing fields — make changes and save.", "info"); };
  const onSubmit = async () => {
    if (!rec) return;
    try {
      const updated = await api.records.update(domain, recordId, { status: "pending" });
      setRec(updated);
      toast("Submitted for approval", "ok");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Submission failed", "error");
    }
  };
  const onMoreAction = (item: string) => {
    setMoreOpen(false);
    if (!rec) return;
    if (item === "Duplicate") {
      api.records.create(domain, { data: { ...rec.data, code: `${rec.id}-copy` } } as any)
        .then((r) => { toast("Duplicated record (draft created)", "ok"); navigate(`/browse/${domain}/${r.id}`); })
        .catch(e => toast(e instanceof Error ? e.message : "Duplicate failed", "error"));
    } else if (item === "Export JSON") {
      const blob = new Blob([JSON.stringify(rec, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${rec.id}.json`; a.click();
      toast("Exported JSON", "ok");
    } else if (item === "Deprecate") {
      api.records.update(domain, recordId, { status: "deprecated" })
        .then((r) => { toast("Record deprecated", "warn"); setRec(r); })
        .catch(e => toast(e instanceof Error ? e.message : "Deprecate failed", "error"));
    } else if (item === "View raw") { navigate(`/versions/${domain}/${recordId}`); }
  };

  if (loading) {
    return <div style={{ padding: "28px 40px", color: "var(--md-on-surface-var)" }}>Loading record…</div>;
  }
  if (error || !rec) {
    return (
      <div style={{ padding: "28px 40px" }}>
        <div className="font-mono" style={{ font: "var(--t-mono-sm)", color: "var(--md-tertiary)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8 }}>
          <Link to="/browse" style={{ color: "inherit", textDecoration: "none" }}>Reference data</Link>
          {" · "}
          <Link to={`/browse/${domain}`} style={{ color: "inherit", textDecoration: "none" }}>{domain}</Link>
          {" · "}<span style={{ color: "var(--md-on-surface)" }}>{recordId}</span>
        </div>
        <h1 className="font-display" style={{ font: "var(--t-display-sm)" }}>Record not found</h1>
        <p style={{ color: "var(--md-on-surface-var)" }}>{error ?? `No record ${recordId} in ${domain}.`}</p>
        <Link to={`/browse/${domain}`} style={{ color: "var(--md-primary)" }}>← Back to {domain}</Link>
      </div>
    );
  }

  const recName = String(rec.data?.name ?? rec.id);
  const recOwner = rec.updatedBy ?? rec.createdBy ?? "—";

  return (
    <div style={{ padding: "28px 40px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <Header
        rec={rec}
        recName={recName}
        recOwner={recOwner}
        moreOpen={moreOpen} setMoreOpen={setMoreOpen}
        onEdit={onEdit} onSubmit={onSubmit} onMoreAction={onMoreAction}
      />

      <nav role="tablist" aria-label="Record sections" style={{
        display: "flex", gap: 4, marginTop: 24, marginBottom: 24,
        borderBottom: "1px solid var(--md-outline-var)", flexWrap: "wrap",
      }}>
        {TABS.map(t => {
          const active = t === tab;
          const isLinked = t === "Lineage" || t === "Versions";
          const node = (
            <button
              role="tab"
              aria-selected={active}
              onClick={() => !isLinked && setTab(t)}
              style={{
                appearance: "none", background: "transparent", border: "none",
                padding: "12px 18px", cursor: "pointer",
                font: "var(--t-title-sm)",
                color: active ? "var(--md-primary)" : "var(--md-on-surface-var)",
                borderBottom: `2px solid ${active ? "var(--md-primary)" : "transparent"}`,
                transition: "color var(--d-short-2) var(--ease-standard)",
              }}
            >{t}{isLinked ? " ↗" : ""}</button>
          );
          if (isLinked) {
            const href = t === "Lineage" ? `/lineage/${rec.id}` : `/versions/${domain}/${rec.id}`;
            return <Link key={t} to={href} style={{ textDecoration: "none" }}>{node}</Link>;
          }
          return <span key={t}>{node}</span>;
        })}
      </nav>

      {tab === "Overview" && <OverviewTab rec={rec} recOwner={recOwner} />}
      {tab === "Fields"   && <FieldsTab rec={rec} domain={domain} recordId={recordId} onSaved={setRec} />}
      {tab === "Related"  && <RelatedTab />}
      {tab === "Audit"    && <AuditTab />}
    </div>
  );
}

function Header({ rec, recName, recOwner, moreOpen, setMoreOpen, onEdit, onSubmit, onMoreAction }: {
  rec: RecordRow;
  recName: string;
  recOwner: string;
  moreOpen: boolean;
  setMoreOpen: (v: boolean) => void;
  onEdit: () => void;
  onSubmit: () => void;
  onMoreAction: (item: string) => void;
}) {
  const status = rec.status as string;
  return (
    <header>
      <div className="font-mono" style={{
        font: "var(--t-mono-sm)", color: "var(--md-tertiary)",
        textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8,
      }}>
        <Link to="/browse" style={{ color: "inherit", textDecoration: "none" }}>Reference data</Link>
        {" · "}
        <Link to={`/browse/${rec.domainId}`} style={{ color: "inherit", textDecoration: "none" }}>{rec.domainId}</Link>
        {" · "}
        <span style={{ color: "var(--md-on-surface)" }}>{rec.id}</span>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
        <div>
          <h1 className="font-display" style={{
            margin: 0, font: "var(--t-display-sm)", letterSpacing: "-0.025em",
            fontStyle: "italic", fontWeight: 400,
          }}>{recName}</h1>
          <div style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
            <Pill tone={STATUS_TONE[status] ?? "neutral"}>● {status}</Pill>
            <Pill tone="neutral" mono>v{rec.version}</Pill>
            <span className="font-mono" style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>
              Updated {fmtRelative(rec.updatedAt)} by {recOwner}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", position: "relative" }}>
          <button style={btnOutlined()} onClick={onEdit}>Edit</button>
          <button style={btnFilled()} onClick={onSubmit}>Submit for approval</button>
          <button
            aria-haspopup="menu" aria-expanded={moreOpen} aria-label="More actions"
            onClick={() => setMoreOpen(!moreOpen)}
            style={{ ...btnOutlined(), padding: "0 14px", minWidth: 0 }}
          >⋯</button>
          {moreOpen && (
            <div role="menu" style={{
              position: "absolute", top: "100%", right: 0, marginTop: 6,
              background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)",
              borderRadius: "var(--r-md)", boxShadow: "var(--e-3)", minWidth: 200, padding: 6, zIndex: 20,
            }}>
              {["Duplicate", "Export JSON", "Deprecate", "View raw"].map(item => (
                <button key={item} role="menuitem" onClick={() => onMoreAction(item)} style={menuItem()}>{item}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function OverviewTab({ rec, recOwner }: { rec: RecordRow; recOwner: string }) {
  const tags = rec.tags ?? [];
  const dataEntries = Object.entries(rec.data ?? {});
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)", gap: 20 }}>
      <div style={{ display: "grid", gap: 20 }}>
        <Card>
          <SectionEyebrow>Identifier</SectionEyebrow>
          <p style={{ font: "var(--t-body-lg)", margin: "8px 0 0", color: "var(--md-on-surface)" }}>
            <code style={{ font: "var(--t-mono-md)" }}>{rec.domainId}/{rec.id}</code>
          </p>
        </Card>
        <Card>
          <SectionEyebrow>Data</SectionEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, marginTop: 12 }}>
            {dataEntries.length === 0 ? <span style={{ color: "var(--md-on-surface-var)" }}>No fields.</span> : null}
            {dataEntries.map(([k, v]) => (
              <div key={k}>
                <div style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{k}</div>
                <div className="font-mono" style={{ font: "var(--t-mono-md)", marginTop: 4, wordBreak: "break-word" }}>{String(v ?? "—")}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gap: 20 }}>
        <Card>
          <SectionEyebrow>Owner</SectionEyebrow>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
            <Avatar name={recOwner} size={44} />
            <div>
              <div style={{ font: "var(--t-title-md)" }}>{recOwner}</div>
              <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>{rec.domainId}</div>
            </div>
          </div>
        </Card>
        {rec.qualityScore != null && (
          <Card>
            <SectionEyebrow>Data quality</SectionEyebrow>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
              <span className="font-display tabular" style={{ font: "var(--t-display-sm)", fontStyle: "italic", fontWeight: 400 }}>{rec.qualityScore}</span>
              <span style={{ font: "var(--t-body-md)", color: "var(--md-on-surface-var)" }}>/ 100</span>
            </div>
            <div style={{ height: 8, marginTop: 12, background: "var(--md-sc-low)", borderRadius: "var(--r-pill)", overflow: "hidden" }}>
              <div style={{ width: `${rec.qualityScore}%`, height: "100%", background: "var(--success)" }} />
            </div>
          </Card>
        )}
        {tags.length > 0 && (
          <Card>
            <SectionEyebrow>Tags</SectionEyebrow>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {tags.map(t => <Pill key={t} tone="tertiary">{t}</Pill>)}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function FieldsTab({ rec, domain, recordId, onSaved }: { rec: RecordRow; domain: string; recordId: string; onSaved: (r: RecordRow) => void }) {
  const initial = useMemo(() => Object.fromEntries(Object.entries(rec.data ?? {}).map(([k, v]) => [k, String(v ?? "")])), [rec]);
  const [draft, setDraft] = useState<Record<string, string>>(initial);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const onDiscard = () => {
    setDraft(initial);
    toast("Changes discarded", "info");
  };
  const onSave = async () => {
    setSaving(true);
    try {
      const updated = await api.records.update(domain, recordId, { data: draft });
      onSaved(updated);
      toast("Saved", "ok");
    } catch (e) {
      toast(e instanceof Error ? `Save failed: ${e.message}` : "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const keys = Object.keys(initial);

  return (
    <Card>
      <SectionEyebrow>Editable fields</SectionEyebrow>
      <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
        {keys.length === 0 && <span style={{ color: "var(--md-on-surface-var)" }}>This record has no fields yet.</span>}
        {keys.map(k => (
          <div key={k} style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 24, alignItems: "start" }}>
            <div>
              <label htmlFor={`f-${k}`} style={{ font: "var(--t-label-lg)" }}>{k}</label>
            </div>
            <input
              id={`f-${k}`}
              value={draft[k] ?? ""}
              onChange={e => setDraft({ ...draft, [k]: e.target.value })}
              style={input(false, false)}
            />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 24, justifyContent: "flex-end" }}>
        <button style={btnOutlined()} onClick={onDiscard} disabled={!dirty || saving}>Discard</button>
        <button style={btnFilled()} onClick={onSave} disabled={saving || !dirty}>{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </Card>
  );
}

function RelatedTab() {
  return (
    <Card>
      <SectionEyebrow>Related records</SectionEyebrow>
      <p style={{ marginTop: 12, color: "var(--md-on-surface-var)" }}>Related-record graph isn't wired up yet for this record. Use the Lineage tab for a visual dependency view.</p>
    </Card>
  );
}

function AuditTab() {
  return (
    <Card>
      <SectionEyebrow>Audit timeline</SectionEyebrow>
      <p style={{ marginTop: 12, color: "var(--md-on-surface-var)" }}>See the global <Link to="/audit" style={{ color: "var(--md-primary)" }}>audit log</Link> for change history.</p>
    </Card>
  );
}

function SectionEyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono" style={{
      font: "var(--t-label-md)", color: "var(--md-on-surface-var)",
      textTransform: "uppercase", letterSpacing: "0.12em",
    }}>{children}</div>
  );
}

function btnFilled(): CSSProperties {
  return {
    appearance: "none", border: "none", cursor: "pointer",
    background: "var(--md-primary)", color: "var(--md-on-primary)",
    font: "var(--t-label-lg)", height: 40, padding: "0 20px",
    borderRadius: "var(--r-pill)",
    transition: "transform var(--d-short-2) var(--ease-spring-soft)",
  };
}
function btnOutlined(): CSSProperties {
  return {
    appearance: "none", cursor: "pointer",
    background: "transparent", color: "var(--md-on-surface)",
    border: "1px solid var(--md-outline)",
    font: "var(--t-label-lg)", height: 40, padding: "0 20px",
    borderRadius: "var(--r-pill)",
  };
}
function menuItem(): CSSProperties {
  return {
    display: "block", width: "100%", textAlign: "left",
    appearance: "none", border: "none", background: "transparent", cursor: "pointer",
    padding: "10px 12px", borderRadius: "var(--r-sm)", font: "var(--t-body-md)",
    color: "var(--md-on-surface)",
  };
}
function input(mono: boolean, error: boolean): CSSProperties {
  return {
    width: "100%", boxSizing: "border-box",
    height: 40, padding: "0 14px",
    background: "var(--md-sc-low)",
    border: `1px solid ${error ? "var(--danger)" : "var(--md-outline-var)"}`,
    borderRadius: "var(--r-sm)",
    font: mono ? "var(--t-mono-md)" : "var(--t-body-md)",
    color: "var(--md-on-surface)",
    outline: "none",
  };
}

export default RecordDetail;
